import React, { useState, useEffect } from 'react'
import { useIsMounted } from '../helpers/hooks'
import { useNetworkStatus } from './networkStatus'
import {
    fetchPendingRequests,
    handleDeleteRequest,
    hasPendingRequests,
} from './pendingRequestHelpers'
import useRequest from '../hooks/useRequest'
import useAlert from '../hooks/useAlert'
import useAuth from '../hooks/useAuth'
import { parse } from 'qs'
import { useHistory } from 'react-router-dom'
import useFleet from '../hooks/useFleet'
import { onGettedAssetLocationInfo } from './onGettedAssetLocationInfo'
import { handleOnCancel, handleOnDone } from './handleOnDone'
import ExpiredRequestsBanner from '../components/ExpiredRequestsBanner/ExpiredRequestsBanner'
import useHome from '../hooks/useHome'
import {
    updatePendingRequestAttempt,
    updatePendingRequestStatus,
} from '../../../indexedDb/pendingRequests'
import ReviewRequestBanner from '../components/ReviewRequestBanner/ReviewRequestBanner'
import {
    checkReviewRequestExists,
    saveReviewRequest,
} from '../../../indexedDb/reviewRequests'
import { sendEventToIntercom } from '../helpers/util'
import {
    trackPartsRequestCreatedOffline,
    trackPendingOfflineRequests,
    trackRepairRequestCreatedOffline,
} from './sendGAAnalyticsEvent'
import useUploadFile from '../hooks/useUploadFile'
import { base64ToFile } from './processImagesHelper'

let isProcessing = false
const PendingRequestProcessor = () => {
    const [showExpiredBanner, setShowExpiredBanner] = useState(false)
    const [showReviewRequestBanner, setShowReviewRequestBanner] = useState(
        false,
    )
    const isMounted = useIsMounted()
    const { isOffline } = useNetworkStatus()
    const history = useHistory()
    const {
        handleCreateServiceRequest,
        handleCreatePartsRequest,
        changeNeedsUpdate,
    } = useRequest()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const { user } = useAuth()
    const { handleGetAsset } = useFleet()
    const { handleSendNotification } = useHome()
    const { handleUpdateFile } = useUploadFile()
    const isEstimate = parse(window.location.search, {
        ignoreQueryPrefix: true,
    })?.isEstimate

    const onCreatedRequestSuccess = response => {
        const requestId = response?.data?.response?.salesforceId
        // // Leaving the if here in case the modal comes back
        // const hasCreditReview = response?.data?.response?.creditReview
        // if (hasCreditReview) {
        //     handleChangeAlert({
        //         title: `${
        //             isEstimate ? 'Estimate' : 'Service'
        //         } Request Pending Credit Review`,
        //         message: `Thank you for your request.  It is currently undergoing a credit review and will be processed once the review is complete.`,
        //         textOk: 'View Request',
        //         onDone: () => {
        //             handleOnDone(history, requestId, handleChangeOpen, 'Repair')
        //         },
        //         type: 'question',
        //     })
        // } else {

        handleChangeAlert({
            title: isEstimate
                ? 'Your request has been sent'
                : 'Your Repair Request has been sent',
            message: isEstimate
                ? 'Check back to follow the status of your estimate'
                : 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                handleOnDone(history, requestId, handleChangeOpen, 'Repair')
            },
            type: 'success',
        })
    }

    const onCatchError = error => {
        console.log(error)
    }

    const onCreatedRequestError = async (request, currentAttemptCount) => {
        const { id } = request
        try {
            await updatePendingRequestAttempt(id, currentAttemptCount)
        } catch (error) {
            console.log(error)
        }
    }

    const onCreatedPartRequestSuccess = async (response, id) => {
        const requestId = response?.data?.response?.request?.salesforceId
        sendEventToIntercom('successful-parts-quote-request')
        changeNeedsUpdate({ dashboard: true, request: true })
        handleDeleteRequest(id)
        handleChangeAlert({
            title: 'Request Created',
            message: 'Check back to see the latest status',
            textOk: 'View Request',
            textCancel: 'Return to Dashboard',
            onCancel: () => handleOnCancel(history),
            onDone: () => {
                handleOnDone(history, requestId, handleChangeOpen, 'Parts')
            },
            type: 'success',
        })
    }

    // This upload all the necessary images and link them to the request and upload to AWS
    const uploadImages = async completeRequest => {
        let request = completeRequest
        if (request?.images.length > 0) {
            let successIds = []
            let images = request?.images
            await Promise.all(
                images?.map(image => {
                    const file = base64ToFile(image.data, image.name)
                    const formData = new FormData()
                    formData.append('file', file)

                    // This makes that we wait for every upload before continuing
                    return new Promise((resolve, reject) => {
                        handleUpdateFile(
                            formData,
                            res => {
                                const imageId = res?.data?.filename
                                successIds.push(imageId)
                                resolve()
                            },
                            error => {
                                console.log('Error', error)
                                reject(error)
                            },
                        )
                    })
                }),
            )
            // Parts has 1 images and server can have multiple
            if (request?.type === 'Parts') {
                request.photoId = successIds[0]
            } else {
                request.photoIds = successIds
            }
        }
        return request
    }

    const getCompleteRequest = async (request, showType) => {
        const paramsToGetFleetAsset = {
            assetId: request.equipmentSFId,
            companyId: user?.company?.id,
            getRequests: true,
        }
        return handleGetAsset(
            paramsToGetFleetAsset,
            response => {
                const locationInfo = onGettedAssetLocationInfo(response)
                if (showType === 'Repair') {
                    request.shipToAccountId =
                        response?.data?.asset?.shipto?.external_id
                }
                const updatedAssetInfo = {
                    ...request,
                    serviceLocationId: locationInfo.serviceLocationId,
                    isEstimate: false,
                    photoIds: [],
                    locationDetails: request.locationDetails,
                }
                return updatedAssetInfo
            },
            onCatchError,
        )
    }

    const onCreatedRequest = async (response, id) => {
        onCreatedRequestSuccess(response)
        handleDeleteRequest(id)
        changeNeedsUpdate({ dashboard: true, request: true })
    }

    const reviewRequestBanner = () => {
        setShowReviewRequestBanner(true)
        localStorage.setItem('reviewRequestsShown', 'true')
    }

    const processPendingRequests = async () => {
        if (isProcessing) return
        isProcessing = true
        try {
            const pendingRequests = await fetchPendingRequests()
            const result = await hasPendingRequests()
            if (!result) return
            result &&
                trackPendingOfflineRequests(
                    user?.external_contact_id,
                    result?.length,
                )

            const now = Date.now()
            // Separate expired and valid requests
            const expiredRequests = []
            const validRequests = []
            const reviewRequests = []

            for (const request of pendingRequests) {
                const { id, status } = request
                const ageInHours = (now - id) / (1000 * 60 * 60)
                if (ageInHours >= 96) {
                    expiredRequests.push(request)
                } else if (status === 'Needs Review') {
                    reviewRequests.push(request)
                } else {
                    validRequests.push(request)
                }
            }

            // Handle expired requests
            for (const request of expiredRequests) {
                const { id } = request
                try {
                    handleDeleteRequest(id)
                    console.log(`Deleted expired request with ID: ${id}`)
                } catch (error) {
                    console.error(
                        `Failed to delete expired request with ID ${id}:`,
                        error,
                    )
                }
            }

            // Show modal/banner for expired requests
            if (expiredRequests.length > 0 && !isOffline) {
                setShowExpiredBanner(true)
                localStorage.setItem('expiredRequestsShown', 'true')
            }

            for (const request of reviewRequests) {
                const exists = await checkReviewRequestExists(request.id)

                if (!exists) {
                    const data = {
                        message: `User failed to sync offline created service request, Equipment Salesforce ID: ${request.equipmentSFId}`,
                        userId: request.userId,
                    }

                    handleSendNotification(
                        data,
                        async () => {
                            reviewRequestBanner()
                            await saveReviewRequest(request.id)
                        },
                        error => console.log(error),
                    )
                }
            }

            //handle valid requests
            for (const request of validRequests) {
                const { id, attemptCount, showType, ...requestInfo } = request
                let currentAttemptCount = attemptCount || 0
                if (currentAttemptCount >= 2) {
                    try {
                        await updatePendingRequestStatus(id, {
                            status: 'Needs Review',
                            error: 'Maximum attempts reached',
                        })
                    } catch (error) {
                        console.error(
                            `Failed to update status/send notification for request ${id}:`,
                            error,
                        )
                    }
                }

                try {
                    const completeRequestNoImages = await getCompleteRequest(
                        requestInfo,
                        showType,
                    )
                    const completeRequest = await uploadImages(
                        completeRequestNoImages,
                    )

                    if (showType === 'Repair') {
                        await handleCreateServiceRequest(
                            completeRequest,
                            response => {
                                trackRepairRequestCreatedOffline(
                                    response,
                                    user?.external_contact_id,
                                    request,
                                )
                                onCreatedRequest(response, id)
                            },
                            onCreatedRequestError(request, currentAttemptCount),
                        )
                    } else if (showType === 'Parts') {
                        await handleCreatePartsRequest(
                            completeRequest,
                            response => {
                                trackPartsRequestCreatedOffline(
                                    response,
                                    user?.external_contact_id,
                                    request,
                                )
                                onCreatedPartRequestSuccess(response, id)
                            },
                            onCreatedRequestError(request, currentAttemptCount),
                        )
                    } else {
                        console.warn(
                            `Unknown showType "${showType}" for request ${id}`,
                        )
                    }
                } catch (error) {
                    console.error(
                        `Failed to process request with ID ${id}:`,
                        error,
                    )
                }
            }
        } catch (error) {
            console.error('Error processing pending requests:', error)
        } finally {
            isProcessing = false
        }
    }

    useEffect(() => {
        if (!isOffline && isMounted) {
            const intervalId = setInterval(() => {
                processPendingRequests()
            }, 60000) // 60 seconds
            return () => {
                clearInterval(intervalId)
            }
        }
    }, [isOffline, isMounted])

    return showExpiredBanner ? (
        <ExpiredRequestsBanner setShowExpiredBanner={setShowExpiredBanner} />
    ) : showReviewRequestBanner ? (
        <ReviewRequestBanner
            setShowReviewRequestBanner={setShowReviewRequestBanner}
        />
    ) : null
}

export default PendingRequestProcessor
